import { useCallback, useEffect, useState } from "react";
import { ChefHat, Download, Home, LogOut, MessageCircle, Package, Share, X } from "lucide-react";
import { toast } from "sonner";
import { api, adminApi } from "@/lib/api";
import { usePWAManifest } from "@/hooks/usePWAManifest";
import { InventoryTab } from "@/components/inventory/InventoryTab";
import { RecipeMappingTab } from "@/components/inventory/RecipeMappingTab";
import { ManagerHome } from "@/components/manager/ManagerHome";
import { ChatTab } from "@/pages/AdminPage";

const KEY = "bk_manager_session";
const pinInput = "w-full px-4 py-3 rounded-2xl border-2 border-stone-200 bg-white text-2xl font-mono tracking-[0.5em] text-center outline-none focus:border-brand-red";

function InstallButton() {
  const pwa = usePWAManifest({ manifest: "/manifest-manager.json", themeColor: "#1C1917", appleTitle: "BK Manager" });
  const [help, setHelp] = useState(false);
  if (pwa.isStandalone) return null;
  const onClick = async () => {
    if (pwa.installEvent) { if (!(await pwa.install())) setHelp(true); } else setHelp(true);
  };
  return (
    <>
      <button type="button" onClick={onClick} data-testid="manager-install-btn"
        className="flex items-center gap-1.5 bg-brand-yellow text-stone-900 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-black active:scale-95">
        <Download className="w-3.5 h-3.5" /> Install
      </button>
      {help && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-4" onClick={() => setHelp(false)} data-testid="manager-install-help">
          <div className="bg-white text-stone-800 rounded-3xl p-6 w-full max-w-sm space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="font-black text-lg flex items-center gap-2"><Download className="w-5 h-5 text-brand-red" /> Install BK Manager</div>
              <button type="button" onClick={() => setHelp(false)} aria-label="Close" className="text-stone-400"><X className="w-5 h-5" /></button>
            </div>
            {pwa.isIOS ? (
              <ol className="text-sm list-decimal pl-5 space-y-2"><li>Tap the <Share className="w-4 h-4 inline -mt-1" /> <b>Share</b> button in Safari.</li><li>Tap <b>Add to Home Screen</b>, then <b>Add</b>.</li></ol>
            ) : (
              <ol className="text-sm list-decimal pl-5 space-y-2"><li>Open the browser menu (<b>⋮</b>).</li><li>Tap <b>Install app</b> or <b>Add to Home screen</b>.</li></ol>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function Login({ onLogin }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/manager/login", { pin });
      localStorage.setItem(KEY, JSON.stringify({ token: data.token, name: data.employee.name }));
      toast.success(data.punch ? `Clocked in — welcome, ${data.employee.name.split(" ")[0]}!` : `Welcome back, ${data.employee.name.split(" ")[0]}`);
      onLogin({ token: data.token, name: data.employee.name });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't sign in");
      setPin("");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="flex-1 flex flex-col justify-center px-6 max-w-sm w-full mx-auto space-y-4" data-testid="manager-login">
      <div className="w-16 h-16 rounded-3xl bg-brand-red text-white flex items-center justify-center"><ChefHat className="w-8 h-8" /></div>
      <div>
        <h1 className="text-2xl font-black text-stone-900">Manager</h1>
        <p className="text-stone-500 text-sm mt-1">Enter your 4-digit PIN. Signing in clocks you in for the shift.</p>
      </div>
      <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" type="password" autoComplete="off" placeholder="••••" data-testid="manager-pin" className={pinInput} required />
      <button type="submit" disabled={busy || pin.length !== 4} data-testid="manager-login-submit"
        className="w-full bg-brand-red text-white py-4 rounded-full text-sm uppercase tracking-widest font-black disabled:opacity-40">{busy ? "Checking…" : "Clock in & open"}</button>
    </form>
  );
}

const TABS = [
  { key: "home", label: "Home", Icon: Home },
  { key: "inventory", label: "Inventory", Icon: Package },
  { key: "recipes", label: "Recipes", Icon: ChefHat },
  { key: "chat", label: "Chat", Icon: MessageCircle },
];

export default function ManagerPage() {
  const [session, setSession] = useState(() => { try { return JSON.parse(localStorage.getItem(KEY) || "null"); } catch { return null; } });
  const [tab, setTab] = useState("home");
  const [invView, setInvView] = useState("overview");
  const token = session?.token;

  const signOut = useCallback((msg) => {
    if (token) adminApi(token).post("/admin/logout").catch(() => {});
    localStorage.removeItem(KEY);
    setSession(null);
    setTab("home");
    if (msg) toast.error(msg);
  }, [token]);

  useEffect(() => {
    if (!token) return;
    adminApi(token).get("/manager/me").catch((e) => { if ([401, 403].includes(e?.response?.status)) signOut(e?.response?.data?.detail); });
  }, [token, signOut]);

  const goTo = (t, view) => { setTab(t); if (view) setInvView(view); };

  return (
    <div className="min-h-screen bg-stone-100 flex flex-col" data-testid="manager-page" style={{ paddingTop: "env(safe-area-inset-top)" }}>
      <header className="bg-stone-900 text-white">
        <div className="px-4 py-3 flex items-center justify-between gap-2">
          <div className="min-w-0 flex items-center gap-2">
            <ChefHat className="w-5 h-5 text-brand-yellow shrink-0" />
            <div>
              <div className="font-black leading-none whitespace-nowrap">BK Manager</div>
              <div className="text-[10px] uppercase tracking-widest opacity-70 whitespace-nowrap" data-testid="manager-header-name">{session ? session.name : "Blossom's Kitchen"}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <InstallButton />
            {session && (
              <button type="button" onClick={() => signOut()} data-testid="manager-signout" className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-black bg-white/10 px-3 py-1.5 rounded-full"><LogOut className="w-3.5 h-3.5" /> Out</button>
            )}
          </div>
        </div>
        {session && (
          <div className="px-2 flex gap-1" data-testid="manager-tabs">
            {TABS.map(({ key, label, Icon }) => (
              <button key={key} type="button" onClick={() => goTo(key)} data-testid={`manager-tab-${key}`}
                className={`flex-1 py-2.5 text-[10px] sm:text-xs uppercase tracking-widest font-bold flex items-center justify-center gap-1.5 rounded-t-lg ${tab === key ? "bg-stone-100 text-brand-red" : "text-white/70"}`}>
                <Icon className="w-4 h-4" /> <span>{label}</span>
              </button>
            ))}
          </div>
        )}
      </header>
      {!session ? <Login onLogin={setSession} /> : (
        <div className="p-3 sm:p-5 max-w-5xl w-full mx-auto flex-1">
          {tab === "home" && <ManagerHome token={token} onSignedOut={signOut} goTo={goTo} />}
          {tab === "inventory" && <InventoryTab key={invView} token={token} initialView={invView} role="manager" />}
          {tab === "recipes" && <RecipeMappingTab token={token} />}
          {tab === "chat" && <ChatTab token={token} />}
        </div>
      )}
    </div>
  );
}
